import axios from 'axios';
import { PrintOption } from '../types';
import { getCurrentSiteKey, normalizeSiteKey, SITE_CHANGE_EVENT, SiteKey } from './site';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';
const TOKEN_KEY = 'authToken';

export const getAuthToken = (): string | null => {
    return localStorage.getItem(TOKEN_KEY);
};

export const setAuthToken = (token: string | null) => {
    if (token) {
        localStorage.setItem(TOKEN_KEY, token);
        axios.defaults.headers.common.Authorization = `Bearer ${token}`;
    } else {
        localStorage.removeItem(TOKEN_KEY);
        delete axios.defaults.headers.common.Authorization;
    }
};

const existingToken = getAuthToken();
if (existingToken) {
    axios.defaults.headers.common.Authorization = `Bearer ${existingToken}`;
}

const setSiteHeader = (siteKey: SiteKey) => {
    axios.defaults.headers.common['x-site-key'] = siteKey;
};

setSiteHeader(getCurrentSiteKey());

window.addEventListener(SITE_CHANGE_EVENT, (event) => {
    const detail = event instanceof CustomEvent ? event.detail : null;
    setSiteHeader(normalizeSiteKey(detail || getCurrentSiteKey()));
});

axios.interceptors.request.use((config) => {
    const headers = config.headers || {};
    headers['x-site-key'] = getCurrentSiteKey();
    config.headers = headers;
    return config;
});

export const fetchGalleries = async () => {
    const response = await axios.get(`${API_URL}/galleries`);
    return response.data;
};

export const fetchCurrentSiteSettings = async (siteKey?: SiteKey) => {
    const response = await axios.get(`${API_URL}/site-settings`, {
        headers: siteKey ? { 'x-site-key': siteKey } : undefined,
    });
    return response.data;
};

export const updateCurrentSiteSettings = async (
    data: {
        name?: string;
        tagline?: string | null;
        theme?: {
            primaryColor?: string | null;
            backgroundColor?: string | null;
            textColor?: string | null;
            logoMaxHeight?: number | string | null;
            borderRadius?: number | string | null;
        };
    },
    siteKey?: SiteKey
) => {
    const response = await axios.patch(`${API_URL}/site-settings`, data, {
        headers: siteKey ? { 'x-site-key': siteKey } : undefined,
    });
    return response.data;
};

export const uploadCurrentSiteLogo = async (logo: File, siteKey?: SiteKey) => {
    const formData = new FormData();
    formData.append('logo', logo);

    const response = await axios.post(`${API_URL}/site-settings/logo`, formData, {
        headers: {
            'Content-Type': 'multipart/form-data',
            ...(siteKey ? { 'x-site-key': siteKey } : {}),
        },
    });
    return response.data;
};

export const deleteCurrentSiteLogo = async (siteKey?: SiteKey) => {
    const response = await axios.delete(`${API_URL}/site-settings/logo`, {
        headers: siteKey ? { 'x-site-key': siteKey } : undefined,
    });
    return response.data;
};

export const fetchGalleryById = async (galleryId: string, accessToken?: string | null) => {
    const response = await axios.get(`${API_URL}/galleries/${galleryId}`, {
        headers: accessToken ? { 'x-gallery-access': accessToken } : undefined,
    });
    return response.data;
};

export const fetchGalleryImageDownloadUrl = async (
    galleryId: string,
    imageId: string,
    accessToken?: string | null
) => {
    const response = await axios.get(`${API_URL}/galleries/${galleryId}/images/${imageId}/download`, {
        headers: accessToken ? { 'x-gallery-access': accessToken } : undefined,
    });
    return response.data;
};

export const getGalleryImageDownloadFileUrl = (
    galleryId: string,
    imageId: string,
    accessToken?: string | null
) => {
    const url = new URL(`${API_URL}/galleries/${galleryId}/images/${imageId}/download/file`);
    if (accessToken) {
        url.searchParams.set('accessToken', accessToken);
    }
    return url.toString();
};

export const accessGalleryByPassword = async (galleryId: string, password: string) => {
    const response = await axios.post(`${API_URL}/galleries/${galleryId}/access`, { password });
    return response.data;
};

export const accessGalleryDirectLogin = async (credentials: { username: string; password: string }) => {
    const response = await axios.post(`${API_URL}/galleries/access/direct`, credentials);
    return response.data;
};

export const fetchAllGalleriesAdmin = async () => {
    const response = await axios.get(`${API_URL}/galleries/admin/all`);
    return response.data;
};

export const createGallery = async (data: { title: string; description?: string; isPublic?: boolean; isActive?: boolean; password?: string }) => {
    const response = await axios.post(`${API_URL}/galleries`, data);
    return response.data;
};

export const updateGallery = async (
    galleryId: string,
    data: { title?: string; description?: string; isPublic?: boolean; isActive?: boolean; password?: string }
) => {
    const response = await axios.put(`${API_URL}/galleries/${galleryId}`, data);
    return response.data;
};

export const deleteGallery = async (galleryId: string) => {
    const response = await axios.delete(`${API_URL}/galleries/${galleryId}`);
    return response.data;
};

export const regenerateAiTextsForGallery = async (galleryId: string) => {
    const response = await axios.post(`${API_URL}/galleries/${galleryId}/regenerate-ai`);
    return response.data;
};

export const uploadImage = async (formData: FormData) => {
    const response = await axios.post(`${API_URL}/images/upload`, formData, {
        headers: {
            'Content-Type': 'multipart/form-data',
        },
    });
    return response.data;
};

export const fetchImages = async () => {
    const response = await axios.get(`${API_URL}/images`);
    return response.data;
};

export const fetchImageById = async (imageId: string) => {
    const response = await axios.get(`${API_URL}/images/${imageId}`);
    return response.data;
};

export const fetchImagePrintOptions = async (imageId: string) => {
    const response = await axios.get(`${API_URL}/images/${imageId}/print-options`);
    return response.data;
};

export const saveImagePrintOptions = async (imageId: string, printOptions: PrintOption[]) => {
    const response = await axios.put(`${API_URL}/images/${imageId}/print-options`, { printOptions });
    return response.data;
};

export const createCheckoutSession = async (data: {
    imageId?: string | number;
    printOptionId?: string | number;
    quantity?: number;
    items?: Array<{
        imageId: string | number;
        printOptionId: string | number;
        quantity?: number;
    }>;
    accessToken?: string | null;
}) => {
    const response = await axios.post(`${API_URL}/checkout/sessions`, data, {
        headers: data.accessToken ? { 'x-gallery-access': data.accessToken } : undefined,
    });
    return response.data;
};

export const fetchOrders = async () => {
    const response = await axios.get(`${API_URL}/orders`);
    return response.data;
};

export const updateOrderFulfillmentStatus = async (orderId: number, fulfillmentStatus: string) => {
    const response = await axios.patch(`${API_URL}/orders/${orderId}/fulfillment-status`, { fulfillmentStatus });
    return response.data;
};

export const assignImageToGallery = async (imageId: string, galleryId: string | null) => {
    const response = await axios.patch(`${API_URL}/images/${imageId}/gallery`, { galleryId });
    return response.data;
};

export const updateImageMeta = async (
    imageId: string,
    data: {
        title?: string;
        description?: string;
        showOnHomepage?: boolean;
        showAsVision?: boolean;
        homeBlockIds?: number[];
        aiTextDaConcise?: string;
        aiTextDaExpert?: string;
        aiTextDaDreamy?: string;
        aiTextDaHashtags?: string;
        aiTextEnConcise?: string;
        aiTextEnExpert?: string;
        aiTextEnDreamy?: string;
        aiTextEnHashtags?: string;
    }
) => {
    const response = await axios.patch(`${API_URL}/images/${imageId}/meta`, data);
    return response.data;
};

export const deleteImage = async (imageId: string) => {
    const response = await axios.delete(`${API_URL}/images/${imageId}`);
    return response.data;
};

export const regenerateAiTextsForImage = async (imageId: string) => {
    const response = await axios.post(`${API_URL}/images/${imageId}/regenerate-ai`);
    return response.data;
};

export const fetchVisibleHomeBlocks = async () => {
    const response = await axios.get(`${API_URL}/home-blocks`);
    return response.data;
};

export const fetchHomeBlocksAdmin = async () => {
    const response = await axios.get(`${API_URL}/home-blocks/admin/all`);
    return response.data;
};

export const createHomeBlock = async (data: {
    title: string;
    description?: string;
    isVisible?: boolean;
    sortOrder?: number;
    imageIds?: number[];
}) => {
    const response = await axios.post(`${API_URL}/home-blocks`, data);
    return response.data;
};

export const updateHomeBlock = async (
    blockId: number,
    data: {
        title?: string;
        description?: string;
        isVisible?: boolean;
        sortOrder?: number;
        imageIds?: number[];
    }
) => {
    const response = await axios.patch(`${API_URL}/home-blocks/${blockId}`, data);
    return response.data;
};

export const uploadHomeBlockVideo = async (blockId: number, video: File) => {
    const formData = new FormData();
    formData.append('video', video);
    const response = await axios.post(`${API_URL}/home-blocks/${blockId}/video`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data;
};

export const deleteHomeBlockVideo = async (blockId: number) => {
    const response = await axios.delete(`${API_URL}/home-blocks/${blockId}/video`);
    return response.data;
};

export const deleteHomeBlock = async (blockId: number) => {
    const response = await axios.delete(`${API_URL}/home-blocks/${blockId}`);
    return response.data;
};

export const fetchGalleryBlocksAdmin = async (galleryId: string) => {
    const response = await axios.get(`${API_URL}/galleries/${galleryId}/blocks/admin`);
    return response.data;
};

export const createGalleryBlock = async (
    galleryId: string,
    data: {
        title: string;
        description?: string;
        isVisible?: boolean;
        sortOrder?: number;
        imageIds?: number[];
    }
) => {
    const response = await axios.post(`${API_URL}/galleries/${galleryId}/blocks`, data);
    return response.data;
};

export const updateGalleryBlock = async (
    galleryId: string,
    blockId: number,
    data: {
        title?: string;
        description?: string;
        isVisible?: boolean;
        sortOrder?: number;
        imageIds?: number[];
    }
) => {
    const response = await axios.patch(`${API_URL}/galleries/${galleryId}/blocks/${blockId}`, data);
    return response.data;
};

export const uploadGalleryBlockVideo = async (galleryId: string, blockId: number, video: File) => {
    const formData = new FormData();
    formData.append('video', video);
    const response = await axios.post(`${API_URL}/galleries/${galleryId}/blocks/${blockId}/video`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data;
};

export const deleteGalleryBlockVideo = async (galleryId: string, blockId: number) => {
    const response = await axios.delete(`${API_URL}/galleries/${galleryId}/blocks/${blockId}/video`);
    return response.data;
};

export const deleteGalleryBlock = async (galleryId: string, blockId: number) => {
    const response = await axios.delete(`${API_URL}/galleries/${galleryId}/blocks/${blockId}`);
    return response.data;
};

export const loginUser = async (credentials: { email: string; password: string }) => {
    const response = await axios.post(`${API_URL}/auth/login`, credentials);
    return response.data;
};

export const registerUser = async (userData: { username: string; email: string; password: string }) => {
    const response = await axios.post(`${API_URL}/auth/register`, userData);
    return response.data;
};